"use client";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import type { ListDisplayController } from "@/hooks/useListController";
import { downloadTableExport } from "@/utils/table-export-download.client";
import type { TableExportDelivery } from "@/utils/table-export-download.client";
import type { UseMutationResult } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import cn from "twsx";
import { ListDisplayMenu } from "./ListDisplayMenu";
export type ExportMutation = UseMutationResult<TableExportDelivery, unknown, {
  format: "csv" | "xlsx" | "pdf";
  exportTarget: {
    type: "ids";
    ids: string[];
  } | {
    type: "all";
    filters?: unknown;
    search?: string;
  };
  actor?: {
    id: string;
  };
}>;
export function ListDisplayControls<$Filters>({
  currentFilters,
  currentSearch,
  exportMutation,
  display,
  className
}: {
  currentFilters?: $Filters;
  currentSearch?: string;
  exportMutation?: ExportMutation;
  display?: ListDisplayController;
  className?: string;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  if (!display && !exportMutation) {
    return null;
  }
  const startExport = (format: "csv" | "pdf" | "xlsx") => {
    if (!exportMutation || isDownloading) return;
    const download = (async () => {
      setIsDownloading(true);
      try {
        const delivery = await exportMutation.mutateAsync({
          format,
          exportTarget: {
            type: "all",
            filters: currentFilters,
            search: currentSearch
          }
        });
        await downloadTableExport(delivery);
      } finally {
        setIsDownloading(false);
      }
    })();
    toast.promise(download, {
      loading: `Preparing ${format.toUpperCase()}...`,
      success: "Download started",
      error: "Couldn't export. Try again."
    });
  };
  return <div className={cn("ml-auto flex items-center gap-2", className)}>
      {display && <ListDisplayMenu display={display} />}
      {exportMutation && <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label="Export" variant="toolbar" size={32} type="button" disabled={exportMutation.isPending || isDownloading}>
              <Download />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => startExport("csv")}>
              Export all as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => startExport("xlsx")}>
              Export all as XLSX
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => startExport("pdf")}>
              Export all as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>}
    </div>;
}